import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const API = `${process.env.REACT_APP_API_URL}`;

/* ─────────────── Yardımcılar ─────────────── */
const QUARTER_NAMES: Record<number, string> = { 1: 'Ekim - Aralık', 2: 'Ocak - Mart', 3: 'Nisan - Haziran', 4: 'Temmuz - Eylül' };
const toFiscal = (calYear: number, calQ: number) => ({ fq: (calQ % 4) + 1, fy: calQ === 4 ? calYear + 1 : calYear });
const fLabel = (calYear: number, calQ: number) => {
  const { fq, fy } = toFiscal(calYear, calQ);
  return `Q${fq} ${fy}`;
};
const qName = (calYear: number, calQ: number) => QUARTER_NAMES[toFiscal(calYear, calQ).fq];
const fmtCurrency = (n: number) => {
  const v = Math.round(n);
  if (Math.abs(v) >= 1_000_000) return `₺${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `₺${(v / 1_000).toFixed(0)}K`;
  return `₺${v.toLocaleString('tr-TR')}`;
};

type CurrentBlock = { year: number; quarter: number; isOngoing: boolean; ciro: number; hasTarget: boolean; target?: number; pct?: number; met?: boolean };
type PastBlock = { year: number; quarter: number; ciro: number; target: number; pct: number; met: boolean };
type NewCustomerBlock = { year: number; quarter: number; isOngoing: boolean; target: number; actual: number; pct: number; met: boolean };
type AggMetric = { target: number; actual: number; pct: number; met: boolean };
type PersonAgg = { salesman_name: string; revenue: AggMetric | null; newCustomer: AggMetric | null };

/* ─────────────── Konfeti (kütüphanesiz, karta özel) ─────────────── */
const CONFETTI_COLORS = ['#6366F1', '#8B5CF6', '#10B981', '#F59E0B', '#3B82F6', '#EC4899', '#F43F5E', '#0891B2'];
const Confetti: React.FC<{ pieces?: number }> = ({ pieces = 56 }) => {
  const items = useMemo(
    () =>
      Array.from({ length: pieces }, (_, i) => {
        const round = Math.random() > 0.55;
        const size = 6 + Math.random() * 7;
        return {
          id: i,
          left: Math.random() * 100,
          delay: Math.random() * 0.5,
          duration: 1.9 + Math.random() * 1.7,
          color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
          w: size,
          h: round ? size : size * 0.45,
          radius: round ? '50%' : '2px',
          x: (Math.random() - 0.5) * 200,
          r: (Math.random() > 0.5 ? 1 : -1) * (180 + Math.random() * 540),
        };
      }),
    [pieces],
  );
  return (
    <div className="dqt-confetti" aria-hidden="true">
      {items.map((p) => (
        <span
          key={p.id}
          className="dqt-confetti-piece"
          style={{
            left: `${p.left}%`,
            width: p.w,
            height: p.h,
            background: p.color,
            borderRadius: p.radius,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            ['--dqt-cf-x' as any]: `${p.x}px`,
            ['--dqt-cf-r' as any]: `${p.r}deg`,
          }}
        />
      ))}
    </div>
  );
};

/* ─────────────── Hedef gerçekleşme kartı (hedefi olan çeyrek) ─────────────── */
const TargetCard: React.FC<{
  label: string;
  tag: string;
  tagKind: 'ongoing' | 'past';
  year: number;
  quarter: number;
  ciro: number;
  target: number;
  pct: number;
  met: boolean;
}> = ({ label, tag, tagKind, year, quarter, ciro, target, pct, met }) => {
  const accent = met ? '#10B981' : pct >= 75 ? '#F59E0B' : '#EF4444';
  const diff = ciro - target;
  return (
    <div className={`dqt-card${met ? ' met' : ''}`} style={{ borderLeftColor: accent }}>
      {met && <Confetti />}
      <div className="dqt-card-top">
        <div>
          <div className="dqt-card-label">{label}</div>
          <div className="dqt-card-name">
            {fLabel(year, quarter)} · {qName(year, quarter)}
          </div>
          {met && <span className="dqt-badge dqt-badge-met">🎉 Hedef aşıldı, tebrikler!</span>}
        </div>
        <div className="dqt-right">
          <div className="dqt-pct" style={{ color: accent }}>
            %{pct.toFixed(0)}
          </div>
          <span className={`dqt-tag ${tagKind}`}>{tag}</span>
        </div>
      </div>

      <div className="dqt-bar">
        <div className="dqt-bar-fill" style={{ width: `${Math.min(100, pct)}%`, background: accent }} />
      </div>

      <div className="dqt-stats">
        <div className="dqt-stat">
          <div className="dqt-stat-l">Hedef</div>
          <div className="dqt-stat-v">{fmtCurrency(target)}</div>
        </div>
        <div className="dqt-stat">
          <div className="dqt-stat-l">Gerçekleşen</div>
          <div className="dqt-stat-v" style={{ color: accent }}>
            {fmtCurrency(ciro)}
          </div>
        </div>
        <div className="dqt-stat">
          <div className="dqt-stat-l">{diff >= 0 ? 'Hedef Üstü' : 'Kalan'}</div>
          <div className="dqt-stat-v" style={{ color: diff >= 0 ? '#10B981' : '#334155' }}>
            {diff >= 0 ? '+' : ''}
            {fmtCurrency(Math.abs(diff))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── Toplu görünüm: kişi kartı (sadece yönetici kullanıcılar) ─────────────── */
const AggMetricRow: React.FC<{ icon: string; label: string; m: AggMetric; fmtVal: (n: number) => string }> = ({ icon, label, m, fmtVal }) => {
  const accent = m.met ? '#10B981' : m.pct >= 75 ? '#F59E0B' : '#EF4444';
  return (
    <div className="dqt-mini">
      <div className="dqt-mini-top">
        <span className="dqt-mini-label">
          {icon} {label}
        </span>
        <span className="dqt-mini-pct" style={{ color: accent }}>
          {m.met ? '🎉 ' : ''}%{m.pct.toFixed(0)}
        </span>
      </div>
      <div className="dqt-bar" style={{ height: 7, marginBottom: 6 }}>
        <div className="dqt-bar-fill" style={{ width: `${Math.min(100, m.pct)}%`, background: accent }} />
      </div>
      <div className="dqt-mini-sub">
        Hedef: <b>{fmtVal(m.target)}</b> · Gerçekleşen: <b style={{ color: accent }}>{fmtVal(m.actual)}</b>
      </div>
    </div>
  );
};

const PersonCard: React.FC<{ p: PersonAgg }> = ({ p }) => {
  const anyMet = !!(p.revenue?.met || p.newCustomer?.met);
  return (
    <div className={`dqt-card${anyMet ? ' met' : ''}`} style={{ borderLeftColor: anyMet ? '#10B981' : '#6366F1' }}>
      <div className="dqt-person-name">{p.salesman_name}</div>
      {p.revenue && <AggMetricRow icon="💰" label="Ciro Hedefi" m={p.revenue} fmtVal={fmtCurrency} />}
      {p.newCustomer && <AggMetricRow icon="🤝" label="Yeni Müşteri Hedefi" m={p.newCustomer} fmtVal={(n) => `${n} müşteri`} />}
    </div>
  );
};

/* ─────────────── Toplu görünüm karuseli: 2'li sayfalar, 8 sn'de bir sağa otomatik kayar ─────────────── */
const AGG_SLIDE_INTERVAL_MS = 8000;
const AggCarousel: React.FC<{ people: PersonAgg[] }> = ({ people }) => {
  const pages = useMemo(() => {
    const out: PersonAgg[][] = [];
    for (let i = 0; i < people.length; i += 2) out.push(people.slice(i, i + 2));
    return out;
  }, [people]);
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef<number | null>(null);

  // Veri değişince sayfa aralık dışı kalmasın
  useEffect(() => {
    if (page >= pages.length) setPage(0);
  }, [pages.length, page]);

  // Otomatik kaydırma — her sayfa değişiminde 8 sn'lik sayaç yeniden başlar,
  // üzerine gelindiğinde (hover) durur
  useEffect(() => {
    if (pages.length <= 1 || paused) return;
    const t = setInterval(() => setPage((p) => (p + 1) % pages.length), AGG_SLIDE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [pages.length, paused, page]);

  const go = (p: number) => setPage(((p % pages.length) + pages.length) % pages.length);

  return (
    <div className="dqt-carousel" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div
        className="dqt-car-viewport"
        onTouchStart={(e) => {
          touchX.current = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          if (touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          touchX.current = null;
          if (Math.abs(dx) > 40) go(page + (dx < 0 ? 1 : -1));
        }}
      >
        <div className="dqt-car-track" style={{ transform: `translateX(-${page * 100}%)` }}>
          {pages.map((pair, i) => (
            <div key={i} className="dqt-car-slide">
              {pair.map((p) => (
                <PersonCard key={p.salesman_name} p={p} />
              ))}
            </div>
          ))}
        </div>
      </div>
      {pages.length > 1 && (
        <div className="dqt-car-nav">
          <button type="button" className="dqt-car-arrow" onClick={() => go(page - 1)} aria-label="Önceki">
            ‹
          </button>
          <div className="dqt-car-dots">
            {pages.map((_, i) => (
              <button key={i} type="button" className={`dqt-car-dot${i === page ? ' on' : ''}`} onClick={() => go(i)} aria-label={`Sayfa ${i + 1}`} />
            ))}
          </div>
          <button type="button" className="dqt-car-arrow" onClick={() => go(page + 1)} aria-label="Sonraki">
            ›
          </button>
        </div>
      )}
    </div>
  );
};

/* ─────────────── Yeni müşteri hedefi kartı (ilk fatura kayıtları üzerinden) ─────────────── */
const NewCustomerCard: React.FC<{ nc: NewCustomerBlock }> = ({ nc }) => {
  const accent = nc.met ? '#10B981' : nc.pct >= 75 ? '#F59E0B' : '#0891B2';
  const diff = nc.actual - nc.target;
  return (
    <div className={`dqt-card${nc.met ? ' met' : ''}`} style={{ borderLeftColor: accent }}>
      {nc.met && <Confetti />}
      <div className="dqt-card-top">
        <div>
          <div className="dqt-card-label">Bu Çeyrek · Yeni Müşteri Hedefi</div>
          <div className="dqt-card-name">
            {fLabel(nc.year, nc.quarter)} · {qName(nc.year, nc.quarter)}
          </div>
          {nc.met && <span className="dqt-badge dqt-badge-met">🎉 Hedef aşıldı, tebrikler!</span>}
        </div>
        <div className="dqt-right">
          <div className="dqt-pct" style={{ color: accent }}>
            %{nc.pct.toFixed(0)}
          </div>
          <span className="dqt-tag ongoing">Devam eden çeyrek</span>
        </div>
      </div>

      <div className="dqt-bar">
        <div className="dqt-bar-fill" style={{ width: `${Math.min(100, nc.pct)}%`, background: accent }} />
      </div>

      <div className="dqt-stats">
        <div className="dqt-stat">
          <div className="dqt-stat-l">Hedef</div>
          <div className="dqt-stat-v">{nc.target} müşteri</div>
        </div>
        <div className="dqt-stat">
          <div className="dqt-stat-l">Kazanılan</div>
          <div className="dqt-stat-v" style={{ color: accent }}>
            {nc.actual} müşteri
          </div>
        </div>
        <div className="dqt-stat">
          <div className="dqt-stat-l">{diff >= 0 ? 'Hedef Üstü' : 'Kalan'}</div>
          <div className="dqt-stat-v" style={{ color: diff >= 0 ? '#10B981' : '#334155' }}>
            {diff >= 0 ? '+' : ''}
            {Math.abs(diff)} müşteri
          </div>
        </div>
      </div>

      <div className="dqt-nc-note">
        <i className="bi bi-receipt" style={{ marginRight: 6 }} />
        İlk fatura kayıtları üzerinden takip edilir.
      </div>
    </div>
  );
};

/* ─────────────── Ciro kartı (hedef henüz belirlenmemiş çeyrek) ─────────────── */
const CiroCard: React.FC<{ year: number; quarter: number; ciro: number }> = ({ year, quarter, ciro }) => (
  <div className="dqt-card" style={{ borderLeftColor: '#3B82F6' }}>
    <div className="dqt-card-top">
      <div>
        <div className="dqt-card-label">Bu Çeyrek · Şu Ana Kadar</div>
        <div className="dqt-card-name">
          {fLabel(year, quarter)} · {qName(year, quarter)}
        </div>
      </div>
      <span className="dqt-tag ongoing">Devam eden çeyrek</span>
    </div>
    <div className="dqt-ciro-big" style={{ color: '#3B82F6' }}>
      {fmtCurrency(ciro)}
    </div>
    <div className="dqt-note-inline">
      <i className="bi bi-info-circle" style={{ marginRight: 6 }} />
      Bu çeyrek için henüz hedef belirlenmemiş — çeyreklik toplantıda hedefin girildiğinde burada gerçekleşme oranını göreceksin.
    </div>
  </div>
);

/* ─────────────── CSS ─────────────── */
const css = `
.dqt-wrap { background:#fff; border-radius:16px; overflow:hidden; border:1px solid #f1f5f9; box-shadow:0 1px 2px rgba(0,0,0,.03); }
.dqt-hdr { padding:22px 26px 0; display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:12px; margin-bottom:18px; }
.dqt-hdr-title { display:flex; align-items:center; gap:10px; }
.dqt-hdr-icon { width:38px; height:38px; border-radius:10px; background:#EEF2FF; color:#6366F1; display:flex; align-items:center; justify-content:center; font-size:1.05rem; }
.dqt-hdr-h { margin:0; font-size:.95rem; font-weight:700; color:#1e293b; letter-spacing:-.2px; }
.dqt-hdr-s { font-size:.72rem; color:#94a3b8; font-weight:500; margin-top:2px; }
.dqt-personal { display:inline-flex; align-items:center; gap:5px; background:#F5F3FF; color:#7C3AED; padding:4px 11px; border-radius:9999px; font-size:.66rem; font-weight:700; }

.dqt-body { padding:0 26px 22px; }
.dqt-grid2 { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
@media (max-width: 900px) { .dqt-grid2 { grid-template-columns:1fr; } }
.dqt-single { max-width:560px; }

.dqt-card { position:relative; background:#fff; border:1px solid #f1f5f9; border-left:4px solid #94a3b8; border-radius:14px;
  padding:20px 22px; overflow:visible; height:100%; animation:dqt-in .4s ease both; }
.dqt-card.met { box-shadow:0 0 0 1px #10B98122, 0 1px 3px rgba(0,0,0,.05); animation:dqt-in .4s ease both, dqt-glow 2.4s ease .3s 2; }
@keyframes dqt-in { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
@keyframes dqt-glow { 0%,100% { box-shadow:0 0 0 1px #10B98122, 0 1px 3px rgba(0,0,0,.05); } 50% { box-shadow:0 0 24px #10B98166, 0 6px 20px rgba(0,0,0,.08); } }

.dqt-card-top { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:15px; }
.dqt-card-label { font-size:.68rem; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#64748b; }
.dqt-card-name { font-size:.98rem; font-weight:800; color:#1e293b; letter-spacing:-.02em; margin-top:3px; }
.dqt-right { display:flex; flex-direction:column; align-items:flex-end; gap:6px; flex-shrink:0; }
.dqt-pct { font-size:2rem; font-weight:800; letter-spacing:-.03em; line-height:1; }
.dqt-tag { padding:3px 9px; border-radius:9999px; font-size:.62rem; font-weight:800; white-space:nowrap; }
.dqt-tag.ongoing { background:#EFF6FF; color:#3B82F6; }
.dqt-tag.past { background:#FEF3C7; color:#B45309; }
.dqt-badge { display:inline-block; font-size:.68rem; font-weight:800; padding:3px 10px; border-radius:9999px; margin-top:6px; }
.dqt-badge-met { background:#ECFDF5; color:#10B981; }

.dqt-bar { height:10px; background:#f1f5f9; border-radius:9999px; overflow:hidden; margin-bottom:15px; }
.dqt-bar-fill { height:100%; border-radius:9999px; transform-origin:left center; animation:dqt-grow .9s cubic-bezier(.4,0,.2,1) both; }
@keyframes dqt-grow { from { transform:scaleX(0); } to { transform:scaleX(1); } }

.dqt-stats { display:flex; flex-wrap:wrap; gap:10px; }
.dqt-stat { flex:1; min-width:110px; background:#f8fafc; border-radius:10px; padding:10px 12px; }
.dqt-stat-l { font-size:.66rem; color:#94a3b8; font-weight:600; text-transform:uppercase; letter-spacing:.04em; }
.dqt-stat-v { font-size:1rem; font-weight:800; color:#1e293b; margin-top:2px; }

.dqt-ciro-big { font-size:2rem; font-weight:800; letter-spacing:-.03em; margin:2px 0 12px; }
.dqt-note-inline { font-size:.72rem; color:#94a3b8; line-height:1.5; background:#f8fafc; border-radius:10px; padding:10px 12px; }
.dqt-nc-note { font-size:.68rem; color:#94a3b8; margin-top:12px; }

.dqt-carousel { position:relative; }
.dqt-car-viewport { overflow:hidden; margin:-2px; padding:2px; }
.dqt-car-track { display:flex; transition:transform .6s cubic-bezier(.4,0,.2,1); }
.dqt-car-slide { flex:0 0 100%; min-width:100%; display:grid; grid-template-columns:1fr 1fr; gap:14px; align-items:stretch; }
@media (max-width: 900px) { .dqt-car-slide { grid-template-columns:1fr; } }
.dqt-car-nav { display:flex; align-items:center; justify-content:center; gap:12px; margin-top:14px; }
.dqt-car-dots { display:flex; align-items:center; gap:6px; }
.dqt-car-dot { width:8px; height:8px; padding:0; border:none; border-radius:9999px; background:#e2e8f0; cursor:pointer; transition:all .25s; }
.dqt-car-dot.on { width:22px; background:#6366F1; }
.dqt-car-arrow { width:26px; height:26px; padding:0; border:1px solid #e2e8f0; border-radius:9999px; background:#fff;
  color:#64748b; font-size:1rem; line-height:1; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .2s; }
.dqt-car-arrow:hover { border-color:#6366F1; color:#6366F1; background:#EEF2FF; }

.dqt-person-name { font-size:1rem; font-weight:800; color:#1e293b; letter-spacing:-.02em; margin-bottom:14px; }
.dqt-mini { margin-bottom:14px; }
.dqt-mini:last-child { margin-bottom:0; }
.dqt-mini-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:6px; }
.dqt-mini-label { font-size:.7rem; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#64748b; }
.dqt-mini-pct { font-size:.95rem; font-weight:800; letter-spacing:-.02em; }
.dqt-mini-sub { font-size:.72rem; color:#94a3b8; }
.dqt-mini-sub b { color:#334155; }

.dqt-foot { border-top:1px solid #f1f5f9; padding:12px 26px; display:flex; align-items:center; gap:7px; font-size:.7rem; color:#94a3b8; }
.dqt-foot i { color:#7C3AED; }

.dqt-confetti { position:absolute; left:0; right:0; top:-8px; bottom:0; overflow:visible; pointer-events:none; z-index:6; }
.dqt-confetti-piece { position:absolute; top:0; display:block; will-change:transform, opacity;
  animation-name:dqt-confetti-fall; animation-timing-function:cubic-bezier(.25,.6,.4,1); animation-fill-mode:forwards; }
@keyframes dqt-confetti-fall {
  0% { opacity:0; transform:translate3d(0,-14px,0) rotateZ(0deg); }
  8% { opacity:1; }
  100% { opacity:0; transform:translate3d(var(--dqt-cf-x), 220px, 0) rotateZ(var(--dqt-cf-r)); }
}
`;

/* ─────────────── Ana Widget — sadece giriş yapan kullanıcının kendi verisi ─────────────── */
export const DashboardQuarterlyTargetWidget: React.FC = () => {
  const { data, isLoading, error } = useQuery(
    ['dashboard-quarterly-target-attainment'],
    async () => {
      const res = await axios.get(`${API}/dia/salesman-quarterly-performance/target-attainment`);
      return res.data;
    },
    { refetchOnWindowFocus: false, retry: false },
  );

  const current: CurrentBlock | null = data?.current || null;
  const pastTarget: PastBlock | null = data?.pastTarget || null;
  const newCustomer: NewCustomerBlock | null = data?.newCustomer || null;
  const isAggregate: boolean = !!data?.isAggregate;
  const people: PersonAgg[] = isAggregate ? data?.people || [] : [];

  const PersonalNote = (
    <div className="dqt-foot">
      <i className="bi bi-shield-lock-fill" />
      {isAggregate
        ? 'Bu toplu görünüm yalnızca size özeldir — diğer kullanıcılar sadece kendi hedef durumunu görür.'
        : 'Bu panel yalnızca sana özeldir — herkes sadece kendi çeyreklik hedef ve ciro bilgisini görür.'}
    </div>
  );

  // Veri gelene kadar HİÇBİR ŞEY render etme; ayrıca hata varsa veya kullanıcıya ait
  // hedef yoksa gösterme. Böylece kartı görmemesi gereken kullanıcılarda yükleme
  // sırasında boş/iskelet kart yanıp sönmez — kart sadece hedefi olan kişilere görünür.
  if (isLoading || error || !data || (!current && !people.length)) return null;

  return (
    <div className="col-12 mb-6">
      <style>{css}</style>
      <div className="dqt-wrap">
        {/* Header */}
        <div className="dqt-hdr">
          <div className="dqt-hdr-title">
            <div className="dqt-hdr-icon">🎯</div>
            <div>
              <h3 className="dqt-hdr-h">{isAggregate ? 'Çeyreklik Hedefler · Ekip Görünümü' : 'Çeyreklik Hedeflerim'}</h3>
              <div className="dqt-hdr-s">
                {isAggregate
                  ? `Hedef girilen tüm satışçıların ${fLabel(data.year, data.quarter)} (${qName(data.year, data.quarter)}) durumu`
                  : 'Kişisel çeyreklik ciro ve yeni müşteri hedeflerin, gerçekleşme durumun'}
              </div>
            </div>
          </div>
          <span className="dqt-personal">
            <i className="bi bi-person-lock" />
            {isAggregate ? 'Size özel toplu görünüm' : 'Sana özel'}
          </span>
        </div>

        {/* İçerik */}
        <div className="dqt-body">
          {(() => {
            if (isAggregate) {
              /* Yönetici toplu görünümü: 2'li karusel, 8 sn'de bir otomatik kayar */
              return <AggCarousel people={people} />;
            }
            if (!current) return null;
            const cards: React.ReactNode[] = [];
            if (current.hasTarget) {
              /* İçinde bulunulan çeyreğin ciro hedefi belli → canlı gerçekleşme */
              cards.push(
                <TargetCard
                  key="cur"
                  label="Bu Çeyrek · Ciro Hedef Durumu"
                  tag="Devam eden çeyrek"
                  tagKind="ongoing"
                  year={current.year}
                  quarter={current.quarter}
                  ciro={current.ciro}
                  target={current.target as number}
                  pct={current.pct as number}
                  met={current.met as boolean}
                />,
              );
            } else {
              /* Ciro hedefi henüz yok → bu çeyreğin cirosu + geçmiş çeyreğin hedef durumu */
              cards.push(<CiroCard key="ciro" year={current.year} quarter={current.quarter} ciro={current.ciro} />);
              if (pastTarget) {
                cards.push(
                  <TargetCard
                    key="past"
                    label="Geçen Çeyreğe Ait Hedef Durumu"
                    tag="Geçmiş çeyrek"
                    tagKind="past"
                    year={pastTarget.year}
                    quarter={pastTarget.quarter}
                    ciro={pastTarget.ciro}
                    target={pastTarget.target}
                    pct={pastTarget.pct}
                    met={pastTarget.met}
                  />,
                );
              }
            }
            /* Bu çeyrek için yeni müşteri hedefi varsa durumunu göster */
            if (newCustomer) {
              cards.push(<NewCustomerCard key="nc" nc={newCustomer} />);
            }
            return <div className={cards.length > 1 ? 'dqt-grid2' : 'dqt-single'}>{cards}</div>;
          })()}
        </div>

        {PersonalNote}
      </div>
    </div>
  );
};

export default DashboardQuarterlyTargetWidget;
